import { useEffect, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import {
  getCommissionSum,
  searchCommissions,
  searchSettlements,
} from '../../../Module/Settlement/Presentation/settlementActions';
import {
  selectCommissionSumStatus,
  selectCommissionSumExtra,
  selectCommissionSearchStatus,
  selectCommissionSearchResults,
  selectCommissionSearchMeta,
  selectSettlementSearchStatus,
  selectSettlementSearchResults,
} from '../../../Module/Settlement/Presentation/settlementSelectors';
import { clearCommissionSum, clearCommissionSearch, clearSearchResults } from '../../../Module/Settlement/Presentation/settlementSlice';
import { selectSelectedProject, selectProjects } from '../../../Module/Project/Presentation/projectSelectors';
import { selectUserProfile } from '../../../Module/User/Presentation/userSelectors';
import { getCloudFrontImageUrl } from '../../../Utils/cloudFrontImage';
import type {
  CommissionSumExtra,
  CommissionSearchItem,
  CommissionSearchMeta,
  SettlementItem,
} from '../../../Module/Settlement/Domain/entity/SettlementTypes';
import {
  convertProjectBaseWithFx,
  pickLatestSettlementExchangeRate,
  pickSettlementItemExchangeRate,
  projectRawToBaseUnits,
  resolveNeedsFxConversion,
  resolveProjectBaseForDisplay,
  resolveSinglePaymentDisplay,
  roundDisplayAmount,
  type PaymentDisplayResolution,
} from '../../../Utils/paymentDisplayAmount';
import { settlementUseCases } from '../../../Module/Settlement/Presentation/settlementUseCases';
import type { TFunction } from 'i18next';

const COMMISSION_SEARCH_PAGE_LIMIT = 100;

export type PaymentSummary = {
  promisedAmount: number;
  failedAmount: number;
  completedAmount: number;
  fees: number;
  missionFund: number;
  finalDeposit: number;
  year: number;
  month: number | 'all';
};

export type PaymentMethod = 'cms' | 'card' | 'overseas';
export type PaymentStatusCode = 'complete' | 'fail';

export type PaymentDetail = {
  id: string;
  name: string;
  method: PaymentMethod;
  status: PaymentStatusCode;
  email: string;
  phone: string;
  date: string;
  message: string;
  amount: number;
  fee: number;
  profileImg: string;
  projectTitle?: string;
  donationType?: string;
  paymentDescription?: string;
};

const getDateRange = (year: number, month: number | 'all') => {
  if (month === 'all') {
    return {
      startDate: `${year}-01-01`,
      endDate: `${year}-12-31`,
    };
  }
  const monthStr = String(month).padStart(2, '0');
  const lastDay = new Date(year, month, 0).getDate();
  return {
    startDate: `${year}-${monthStr}-01`,
    endDate: `${year}-${monthStr}-${lastDay}`,
  };
};

const getPaymentMethod = (payType: string): PaymentMethod => {
  const lowerType = payType?.toLowerCase() ?? '';
  // 해외결제(Authorize.Net)는 해외카드로 표시한다.
  if (lowerType.includes('authorize')) {
    return 'overseas';
  }
  if (lowerType.includes('card') || lowerType.includes('thebill')) {
    return 'card';
  }
  return 'cms';
};

const formatDisplayDate = (rawDate?: string) => {
  if (!rawDate) return '';
  const dateObj = new Date(rawDate);
  if (Number.isNaN(dateObj.getTime())) return '';
  return `${dateObj.getFullYear()}.${String(dateObj.getMonth() + 1).padStart(2, '0')}.${String(dateObj.getDate()).padStart(2, '0')}`;
};

const settlementCommissionRawSum = (i: SettlementItem) =>
  (i.kr_card_commission_sum ?? 0) +
  (i.kr_cms_commission_sum ?? 0) +
  (i.us_card_commission_sum ?? 0) +
  (i.us_card_mgmt_sum ?? 0) +
  (i.bank_manage_sum ?? 0) +
  (i.card_manage_sum ?? 0) +
  (i.exchange_manage_sum ?? 0);

const mapCommissionItemsToPaymentDetails = (
  items: CommissionSearchItem[],
  currentProject: { type?: string } | undefined,
  t: TFunction,
): PaymentDetail[] => {
  if (items.length === 0) return [];

  const sorted = [...items].sort((a, b) => {
    const da = a.pay_date ?? '';
    const db = b.pay_date ?? '';
    if (da !== db) return db.localeCompare(da);
    return (a.sponsor_name ?? '').localeCompare(b.sponsor_name ?? '', 'ko');
  });

  const isOneTime = ['OT', 'ONCE', 'ONE_TIME', 'ONETIME'].includes((currentProject?.type ?? '').toUpperCase());
  const donationType = isOneTime ? t('page.newProject.review.fundingType.once') : t('page.newProject.review.fundingType.regular');

  return sorted.map((item, index) => {
    const projectTitle = item.subm?.[0]?.title?.replace(/<br\s*\/?>/gi, ' ') ?? '';
    const formattedDate = formatDisplayDate(item.pay_date);
    const paymentDescription = item.pay_result_comment || item.result_msg || item.comment || '';

    return {
      id: item.mgmt_commission_id ?? `item-${index}`,
      name: item.sponsor_name ?? t('common.labels.anonymous'),
      method: getPaymentMethod(item.pay_type),
      status: item.real_amount > 0 ? 'complete' : 'fail',
      email: item.email ?? '',
      phone: item.sponsor_tel ?? '',
      date: formattedDate,
      message: item.comment ?? item.pay_result_comment ?? '',
      amount: item.real_amount ?? 0,
      fee: item.commission ?? 0,
      profileImg: item.thumbnail ? getCloudFrontImageUrl(item.thumbnail, '84x84') || item.thumbnail : '',
      projectTitle,
      donationType,
      paymentDescription,
    } as PaymentDetail;
  });
};

export const usePaymentStatus = (
  selectedYear: number,
  selectedMonth: number | 'all',
  targetProjectId?: string,
) => {
  const { t } = useTranslation('translation');
  const dispatch = useAppDispatch();
  const userProfile = useAppSelector(selectUserProfile);

  const selectedProject = useAppSelector(selectSelectedProject);
  const projects = useAppSelector(selectProjects);

  // projectmonthlysum 의 월별 후원 합계(raw). null = 결과 없음/실패 → accumulated_amount 폴백
  const [monthlySponsorAmount, setMonthlySponsorAmount] = useState<number | null>(null);

  const projectId = useMemo(() => {
    if (targetProjectId) return targetProjectId;

    if (selectedProject?.project_id) {
      return selectedProject.project_id;
    }
    if (projects.length > 0 && projects[0].project_id) {
      return projects[0].project_id;
    }
    return null;
  }, [targetProjectId, selectedProject, projects]);

  const currentProject = useMemo(() => {
    if (!projectId) return undefined;
    if (selectedProject?.project_id === projectId || selectedProject?._id === projectId) {
      return selectedProject;
    }
    return projects.find((p) => p.project_id === projectId || p._id === projectId);
  }, [projectId, projects, selectedProject]);

  const summaryStatus = useAppSelector(selectCommissionSumStatus);
  const summaryExtra = useAppSelector(selectCommissionSumExtra) as CommissionSumExtra | undefined;
  const settlementSearchStatus = useAppSelector(selectSettlementSearchStatus);
  const settlementSearchResults = useAppSelector(selectSettlementSearchResults) as SettlementItem[] | undefined;

  const detailsStatus = useAppSelector(selectCommissionSearchStatus);
  const detailsResults = useAppSelector(selectCommissionSearchResults) as CommissionSearchItem[] | undefined;
  const detailsMeta = useAppSelector(selectCommissionSearchMeta) as CommissionSearchMeta | undefined;

  const settlementItems = settlementSearchResults ?? [];

  /** 프로필 우선, 비어 있으면 정산 API 행의 은행 국가로 원/달 표시 */
  const displayAsKrw = useMemo(() => {
    const fromProfile = userProfile?.bank_location_country_code;
    const trimmedProfile = fromProfile != null ? String(fromProfile).trim() : '';
    if (trimmedProfile !== '') {
      return trimmedProfile.toUpperCase() === 'KR';
    }
    const fromSettlement = settlementItems[0]?.bank_location_country_code;
    if (fromSettlement != null && String(fromSettlement).trim() !== '') {
      return String(fromSettlement).trim().toUpperCase() === 'KR';
    }
    return true;
  }, [userProfile?.bank_location_country_code, settlementItems]);

  const projectIsUsd = useMemo(
    () => String(currentProject?.currency_type ?? 'KRW').toUpperCase() === 'USD',
    [currentProject?.currency_type],
  );

  const needsFxConversion = useMemo(() => resolveNeedsFxConversion(displayAsKrw, projectIsUsd), [displayAsKrw, projectIsUsd]);

  const isNewSettlementPeriod = useMemo(() => {
    if (selectedYear > 2025) return true;
    if (selectedYear === 2025 && selectedMonth !== 'all' && selectedMonth >= 7) return true;
    return false;
  }, [selectedYear, selectedMonth]);

  const settlementExchangeRate = useMemo(
    () => (isNewSettlementPeriod ? pickLatestSettlementExchangeRate(settlementItems) : undefined),
    [isNewSettlementPeriod, settlementItems],
  );

  const summaryDisplayCurrency = useMemo((): 'USD' | 'KRW' => {
    if (!needsFxConversion) {
      return displayAsKrw ? 'KRW' : 'USD';
    }
    const rateUsable = settlementExchangeRate != null && settlementExchangeRate > 0;
    if (!rateUsable) {
      return projectIsUsd ? 'USD' : 'KRW';
    }
    return displayAsKrw ? 'KRW' : 'USD';
  }, [needsFxConversion, displayAsKrw, settlementExchangeRate, projectIsUsd]);

  const fetchSummary = useCallback(() => {
    if (!projectId) return;

    if (isNewSettlementPeriod) {
      const params: { fld: string; val: string | number; op: string }[] = [
        // settlement 테이블은 project_id 를 소문자로 저장하므로 소문자로 변환해 조회한다.
        { fld: 'project_id', val: projectId.toLowerCase(), op: 'eq' },
        { fld: 'settle_year', val: selectedYear, op: 'eq' },
      ];
      if (selectedMonth !== 'all') {
        params.push({ fld: 'settle_month', val: selectedMonth, op: 'eq' });
      }
      dispatch(searchSettlements({ params }));
    } else {
      const { startDate, endDate } = getDateRange(selectedYear, selectedMonth);
      dispatch(
        getCommissionSum({
          limit: 1,
          params: [
            { fld: 'projectId', val: projectId, op: 'eq' },
            { fld: 'pay_date', op: 'between', val: startDate, v2: endDate },
          ],
          local_field: 'projectId',
          from: 'projects',
        }),
      );
    }
  }, [dispatch, projectId, selectedYear, selectedMonth, isNewSettlementPeriod]);

  const fetchDetails = useCallback(() => {
    if (!projectId) return;

    const { startDate, endDate } = getDateRange(selectedYear, selectedMonth);

    dispatch(
      searchCommissions({
        params: [
          // mgmtcommission 조회는 projectId 를 소문자로 변환해 조회한다.
          { fld: 'projectId', val: projectId.toLowerCase(), op: 'eq' },
          { fld: 'pay_date', op: 'between', val: startDate, v2: endDate },
        ],
        local_field: 'projectId',
        from: 'projects',
        sort: 'pay_date:desc',
        limit: COMMISSION_SEARCH_PAGE_LIMIT,
      }),
    );
  }, [dispatch, projectId, selectedYear, selectedMonth]);

  const fetchPaymentDetailsAllPages = useCallback(async (): Promise<PaymentDetail[]> => {
    if (!projectId) return [];

    const { startDate, endDate } = getDateRange(selectedYear, selectedMonth);
    const baseRequest = {
      params: [
        // mgmtcommission 조회는 projectId 를 소문자로 변환해 조회한다.
        { fld: 'projectId', val: projectId.toLowerCase(), op: 'eq' },
        { fld: 'pay_date', op: 'between', val: startDate, v2: endDate },
      ],
      local_field: 'projectId',
      from: 'projects',
      sort: 'pay_date:desc',
    } as const;

    const fetchByTotal = async (totalElements: number, prefetched?: CommissionSearchItem[]) => {
      if (totalElements <= 0) return [] as CommissionSearchItem[];
      if (prefetched && prefetched.length >= totalElements) return prefetched;

      const res = await settlementUseCases.searchCommissions.execute({
        ...baseRequest,
        limit: totalElements,
        offset: 0,
      });
      return res.data ?? [];
    };

    const knownTotal = detailsMeta?.totalElements;
    if (knownTotal != null && knownTotal > 0) {
      const items = await fetchByTotal(knownTotal);
      return mapCommissionItemsToPaymentDetails(items, currentProject, t);
    }

    const probe = await settlementUseCases.searchCommissions.execute({
      ...baseRequest,
      limit: COMMISSION_SEARCH_PAGE_LIMIT,
      offset: 0,
    });
    const totalElements = probe.meta?.totalElements ?? probe.data?.length ?? 0;
    const items = await fetchByTotal(totalElements, probe.data ?? []);
    return mapCommissionItemsToPaymentDetails(items, currentProject, t);
  }, [projectId, selectedYear, selectedMonth, currentProject, t, detailsMeta?.totalElements]);

  useEffect(() => {
    if (projectId) {
      fetchSummary();
      fetchDetails();
    }
  }, [projectId, selectedYear, selectedMonth, fetchSummary, fetchDetails]);

  // 신 정산기간: projectmonthlysum 에서 projectId + baseYearMonth 일치 행의 sponsorAmountSum 조회.
  // 값이 있으면 약정액으로 사용하고, 없거나 실패 시 accumulated_amount 로 폴백한다.
  useEffect(() => {
    if (!projectId || !isNewSettlementPeriod || selectedMonth === 'all') {
      setMonthlySponsorAmount(null);
      return;
    }
    const baseYearMonth = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}`;
    let cancelled = false;
    setMonthlySponsorAmount(null); // 월/프로젝트 변경 시 이전 값 제거(폴백 우선)
    void settlementUseCases.searchProjectMonthlySum
      .execute({
        params: [
          { fld: 'projectId', op: 'eq', val: projectId },
          { fld: 'baseYearMonth', op: 'eq', val: baseYearMonth },
        ],
        limit: 1,
      })
      .then((res) => {
        if (cancelled) return;
        const value = res?.data?.[0]?.sponsorAmountSum;
        setMonthlySponsorAmount(typeof value === 'number' && Number.isFinite(value) ? value : null);
      })
      .catch(() => {
        if (cancelled) return;
        setMonthlySponsorAmount(null);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, isNewSettlementPeriod, selectedYear, selectedMonth]);

  useEffect(() => {
    return () => {
      dispatch(clearCommissionSum());
      dispatch(clearCommissionSearch());
      dispatch(clearSearchResults());
    };
  }, [dispatch]);

  const summary: PaymentSummary = useMemo(() => {
    const accumulatedAmount = (currentProject?.accumulated_amount ?? 0) as number;
    const supportSupportPercent = Number((currentProject?.support_project as { support_percent?: number })?.support_percent ?? 0);

    if (isNewSettlementPeriod) {
      const items = settlementItems;
      const fallbackRate = pickLatestSettlementExchangeRate(items);

      const settlementRawToDisplayUnrounded = (raw: number, row: SettlementItem) => {
        const base = projectRawToBaseUnits(raw, projectIsUsd);
        const rowRate = pickSettlementItemExchangeRate(row) ?? fallbackRate;
        return convertProjectBaseWithFx(base, {
          needsFxConversion: needsFxConversion,
          displayAsKrw,
          projectCurrencyIsUsd: projectIsUsd,
          exchangeRate: rowRate,
        });
      };

      const accumulatedToUnrounded = (raw: number) => {
        const base = projectRawToBaseUnits(raw, projectIsUsd);
        return convertProjectBaseWithFx(base, {
          needsFxConversion: needsFxConversion,
          displayAsKrw,
          projectCurrencyIsUsd: projectIsUsd,
          exchangeRate: fallbackRate,
        });
      };

      const incomeSumUnrounded = items.reduce((s, row) => s + settlementRawToDisplayUnrounded(row.income_sum ?? 0, row), 0);
      const commissionSumUnrounded = items.reduce((s, row) => s + settlementRawToDisplayUnrounded(settlementCommissionRawSum(row), row), 0);
      const sumRealUnrounded = items.reduce(
        (s, row) => s + settlementRawToDisplayUnrounded(row.sum_real_amount ?? row.netincome_sum ?? 0, row),
        0,
      );
      const supportSumUnrounded = items.reduce((s, row) => s + settlementRawToDisplayUnrounded(row.support_sum ?? 0, row), 0);

      // projectmonthlysum 값이 있으면 약정액으로 사용, 없으면 기존 accumulated_amount 폴백.
      // sponsorAmountSum 은 accumulated_amount 와 동일 통화·스케일이라 동일 변환을 그대로 적용한다.
      const promisedRaw = monthlySponsorAmount != null ? monthlySponsorAmount : accumulatedAmount;
      const promisedUnrounded = accumulatedToUnrounded(promisedRaw);

      const promisedAmount = roundDisplayAmount(promisedUnrounded, summaryDisplayCurrency);
      const completedAmount = roundDisplayAmount(incomeSumUnrounded, summaryDisplayCurrency);
      // 완료 − 약정: 약정액이 더 크면 음수(미납)로 표시한다.
      const failedAmount = roundDisplayAmount(incomeSumUnrounded - promisedUnrounded, summaryDisplayCurrency);
      const fees = -roundDisplayAmount(commissionSumUnrounded, summaryDisplayCurrency);
      const missionFund = -roundDisplayAmount(supportSumUnrounded, summaryDisplayCurrency);
      const finalDeposit = roundDisplayAmount(sumRealUnrounded, summaryDisplayCurrency);

      return {
        promisedAmount,
        failedAmount,
        completedAmount,
        fees,
        missionFund,
        finalDeposit,
        year: selectedYear,
        month: selectedMonth,
      };
    }

    const detailCtx = {
      displayAsKrw,
      projectCurrencyIsUsd: projectIsUsd,
      exchangeRate: settlementExchangeRate,
    };

    if (!summaryExtra) {
      const zero = resolveSinglePaymentDisplay(0, detailCtx).value;
      return {
        promisedAmount: resolveSinglePaymentDisplay(accumulatedAmount, detailCtx).value,
        failedAmount: zero,
        completedAmount: zero,
        fees: zero,
        missionFund: zero,
        finalDeposit: zero,
        year: selectedYear,
        month: selectedMonth,
      };
    }

    const sumRealAmount = summaryExtra.sum_real_amount ?? 0;
    const sumManageCommission = summaryExtra.sum_manage_commission ?? 0;
    const sumCommission = summaryExtra.sum_commission ?? 0;
    const sumExchangeManageCommission = summaryExtra.sum_exchange_manage_commission ?? 0;

    const completedRaw = sumRealAmount + sumManageCommission + sumCommission + sumExchangeManageCommission;

    let missionFundPositive: number;
    if (supportSupportPercent > 99) {
      missionFundPositive = supportSupportPercent;
    } else {
      missionFundPositive = (sumRealAmount * supportSupportPercent) / 100;
    }

    const feesRaw = -(sumManageCommission + sumCommission + sumExchangeManageCommission);
    const finalDepositRaw = sumRealAmount - missionFundPositive;

    const promisedAmount = resolveSinglePaymentDisplay(accumulatedAmount, detailCtx).value;
    const completedAmount = resolveSinglePaymentDisplay(completedRaw, detailCtx).value;

    const promisedBase = projectRawToBaseUnits(accumulatedAmount, projectIsUsd);
    const completedBase = projectRawToBaseUnits(completedRaw, projectIsUsd);
    // 완료 − 약정: 약정액이 더 크면 음수(미납)로 표시한다.
    const failedAmount = resolveProjectBaseForDisplay(completedBase - promisedBase, detailCtx).value;

    const fees = resolveSinglePaymentDisplay(feesRaw, detailCtx).value;
    const missionFund = resolveSinglePaymentDisplay(-missionFundPositive, detailCtx).value;
    const finalDeposit = resolveSinglePaymentDisplay(finalDepositRaw, detailCtx).value;

    return {
      promisedAmount,
      failedAmount,
      completedAmount,
      fees,
      missionFund,
      finalDeposit,
      year: selectedYear,
      month: selectedMonth,
    };
  }, [
    summaryExtra,
    settlementItems,
    isNewSettlementPeriod,
    currentProject,
    selectedYear,
    selectedMonth,
    displayAsKrw,
    projectIsUsd,
    needsFxConversion,
    summaryDisplayCurrency,
    settlementExchangeRate,
    monthlySponsorAmount,
  ]);

  const details: PaymentDetail[] = useMemo(
    () => mapCommissionItemsToPaymentDetails(detailsResults ?? [], currentProject, t),
    [detailsResults, t, currentProject],
  );

  const hasData = useMemo(() => {
    if (isNewSettlementPeriod) {
      return details.length > 0 || settlementItems.length > 0;
    }
    return details.length > 0 || (summaryExtra?.project_count ?? 0) > 0;
  }, [isNewSettlementPeriod, details.length, settlementItems.length, summaryExtra?.project_count]);

  const isLoading = useMemo(() => {
    if (isNewSettlementPeriod) {
      return settlementSearchStatus === 'searching' || detailsStatus === 'searching';
    }
    return summaryStatus === 'loading' || detailsStatus === 'searching';
  }, [isNewSettlementPeriod, settlementSearchStatus, summaryStatus, detailsStatus]);

  const effectiveSummaryStatus = isNewSettlementPeriod
    ? settlementSearchStatus === 'searching'
      ? 'loading'
      : settlementSearchStatus === 'searched'
        ? 'loaded'
        : 'idle'
    : summaryStatus;

  const pagination = useMemo(
    () => ({
      totalElements: detailsMeta?.totalElements ?? 0,
      totalPages: detailsMeta?.totalPages ?? 0,
      currentPage: Math.floor((detailsMeta?.msearch?.offset ?? 0) / (detailsMeta?.perPage ?? 10)) + 1,
      perPage: detailsMeta?.perPage ?? 10,
    }),
    [detailsMeta],
  );

  const resolvePaymentDisplayAmount = useCallback(
    (rawAmount: number): PaymentDisplayResolution =>
      resolveSinglePaymentDisplay(rawAmount, {
        projectCurrencyIsUsd: projectIsUsd,
        displayAsKrw,
        exchangeRate: settlementExchangeRate,
      }),
    [projectIsUsd, displayAsKrw, settlementExchangeRate],
  );

  return {
    summary,
    details,
    hasData,

    isLoading,
    summaryStatus: effectiveSummaryStatus,
    detailsStatus,

    pagination,

    fetchSummary,
    fetchDetails,
    fetchPaymentDetailsAllPages,

    projectId,
    currentProject,

    /** 상세 행·엑셀 금액용 — API raw를 표시 통화로 변환 */
    resolvePaymentDisplayAmount,
    /** 요약 카드에 넣을 숫자는 이미 이 통화 기준으로 계산됨 */
    summaryDisplayCurrency,
    /** 화면에 노출할 때 사용할 정산 환율(있을 때만). 배치는 추후 확정 */
    settlementExchangeRate,
    /** 사용자 통화 선호: 한국 은행(KR)이면 원화 표시 */
    displayAsKrw,
    /** 프로젝트 결제 통화가 USD인지 */
    projectCurrencyIsUsd: projectIsUsd,
  };
};

export default usePaymentStatus;
